import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaPlus, FaMinus, FaShoppingCart, FaArrowLeft, FaCreditCard, FaPaypal, FaAmazon, FaGift, FaTag } from 'react-icons/fa';
import { toast } from 'react-toastify';

const CartPage = () => {
    const navigate = useNavigate();
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [promoCode, setPromoCode] = useState('');
    const [promoApplied, setPromoApplied] = useState(false);
    const [promoDiscount, setPromoDiscount] = useState(0);
    const [checkoutStep, setCheckoutStep] = useState(1);
    const [shippingInfo, setShippingInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'US'
    });
    const [paymentMethod, setPaymentMethod] = useState('credit-card');

    // Load cart from localStorage
    useEffect(() => {
        loadCart();
    }, []);

    const loadCart = () => {
        const savedCart = localStorage.getItem('tfm_cart');
        if (savedCart) {
            setCartItems(JSON.parse(savedCart));
        } else {
            // Dummy cart data for demonstration
            const dummyCart = [
                {
                    id: 1,
                    name: 'Super Hero Action Figure',
                    price: 29.99,
                    quantity: 2,
                    image_url: 'https://images.unsplash.com/photo-1608889335941-32ac5f2041b9?w=500',
                    category: 'Action Figures'
                },
                {
                    id: 2,
                    name: 'Wooden Building Blocks',
                    price: 39.99,
                    quantity: 1,
                    image_url: 'https://images.unsplash.com/photo-1596461404969-9ae70f2830c1?w=500',
                    category: 'Building Sets'
                },
                {
                    id: 3,
                    name: 'Teddy Bear',
                    price: 24.99,
                    quantity: 3,
                    image_url: 'https://images.unsplash.com/photo-1563227812-0ea4c22e6cc8?w=500',
                    category: 'Soft Toys'
                }
            ];
            setCartItems(dummyCart);
            localStorage.setItem('tfm_cart', JSON.stringify(dummyCart));
        }
        setLoading(false);
    };

    // Save cart to localStorage
    const saveCart = (items) => {
        localStorage.setItem('tfm_cart', JSON.stringify(items));
        setCartItems(items);
    };

    // Update quantity
    const updateQuantity = (id, newQuantity) => {
        if (newQuantity < 1) return;
        const updatedItems = cartItems.map(item =>
            item.id === id ? { ...item, quantity: newQuantity } : item
        );
        saveCart(updatedItems);
        toast.info('Cart updated');
    };

    // Remove item from cart
    const removeItem = (id) => {
        const updatedItems = cartItems.filter(item => item.id !== id);
        saveCart(updatedItems);
        toast.success('Item removed from cart');
    };

    // Calculate totals
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const shipping = 0;
    const tax = subtotal * 0.1; // 10% tax
    const discount = promoApplied ? subtotal * 0.1 : 0; // 10% discount
    const total = subtotal + shipping + tax - discount;

    // Apply promo code
    const applyPromoCode = () => {
        if (promoCode.toUpperCase() === 'SAVE10') {
            setPromoApplied(true);
            setPromoDiscount(subtotal * 0.1);
            toast.success('Promo code applied! 10% discount');
        } else if (promoCode.toUpperCase() === 'FREESHIP') {
            setPromoApplied(true);
            toast.success('Free shipping applied!');
        } else {
            toast.error('Invalid promo code');
        }
        setPromoCode('');
    };

    // Handle checkout
    const handleCheckout = () => {
        if (cartItems.length === 0) {
            toast.error('Your cart is empty');
            return;
        }
        setCheckoutStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle shipping info submission
    const handleShippingSubmit = (e) => {
        e.preventDefault();
        setCheckoutStep(3);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle payment submission
    const handlePaymentSubmit = (e) => {
        e.preventDefault();
        
        // Create order object
        const order = {
            id: 'ORD-' + Date.now(),
            items: cartItems,
            subtotal,
            shipping,
            tax,
            discount,
            total,
            shippingInfo,
            paymentMethod,
            date: new Date().toISOString(),
            status: 'pending'
        };
        
        // Save order to localStorage
        const orders = JSON.parse(localStorage.getItem('tfm_orders') || '[]');
        orders.push(order);
        localStorage.setItem('tfm_orders', JSON.stringify(orders));
        
        // Clear cart
        localStorage.removeItem('tfm_cart');
        
        toast.success('Order placed successfully!');
        
        // Navigate to order confirmation
        navigate('/order-confirmation', { state: { order } });
    };

    // Go back to cart
    const backToCart = () => {
        setCheckoutStep(1);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading your cart...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 py-16">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative container mx-auto px-4 text-center text-white">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-fade-in-up">
                        {checkoutStep === 1 ? 'Shopping Cart' : checkoutStep === 2 ? 'Shipping Information' : 'Payment'}
                    </h1>
                    <p className="text-xl opacity-90 animate-fade-in-up animation-delay-200">
                        {checkoutStep === 1 
                            ? `You have ${cartItems.length} item${cartItems.length !== 1 ? 's' : ''} in your cart`
                            : checkoutStep === 2 
                            ? 'Tell us where to send your order'
                            : 'Complete your purchase securely'}
                    </p>
                </div>
            </div>

            {/* Checkout Steps */}
            {checkoutStep > 1 && (
                <div className="bg-white border-b">
                    <div className="container mx-auto px-4 py-4">
                        <div className="flex items-center justify-center gap-8">
                            <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${checkoutStep >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-300'}`}>
                                    1
                                </div>
                                <span className="text-sm">Cart</span>
                            </div>
                            <div className="w-16 h-0.5 bg-gray-300"></div>
                            <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${checkoutStep >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-300'}`}>
                                    2
                                </div>
                                <span className="text-sm">Shipping</span>
                            </div>
                            <div className="w-16 h-0.5 bg-gray-300"></div>
                            <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${checkoutStep >= 3 ? 'bg-purple-600 text-white' : 'bg-gray-300'}`}>
                                    3
                                </div>
                                <span className="text-sm">Payment</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto px-4 py-8">
                {checkoutStep === 1 && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Cart Items */}
                        <div className="lg:col-span-2">
                            {cartItems.length === 0 ? (
                                <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                    <div className="text-6xl mb-4">🛒</div>
                                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
                                    <p className="text-gray-600 mb-6">Looks like you haven't added any items yet</p>
                                    <Link to="/products" className="bg-purple-600 text-white px-6 py-3 rounded-lg inline-flex items-center gap-2 hover:bg-purple-700 transition">
                                        <FaArrowLeft /> Continue Shopping
                                    </Link>
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                                    <div className="p-6 border-b">
                                        <h2 className="text-xl font-bold text-gray-800">Cart Items ({cartItems.length})</h2>
                                    </div>
                                    <div className="divide-y">
                                        {cartItems.map((item) => (
                                            <div key={item.id} className="p-6 flex flex-col sm:flex-row gap-4">
                                                <div className="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                                    {item.image_url ? (
                                                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-4xl">🧸</div>
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                                                        <div>
                                                            <Link to={`/products/${item.id}`} className="text-lg font-semibold text-gray-800 hover:text-purple-600 transition">
                                                                {item.name}
                                                            </Link>
                                                            <p className="text-sm text-gray-500 mt-1">{item.category}</p>
                                                            <p className="text-lg font-bold text-purple-600 mt-2">£{item.price}</p>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-3">
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                                    className="w-8 h-8 border rounded-lg hover:bg-gray-50 flex items-center justify-center"
                                                                >
                                                                    <FaMinus className="text-sm" />
                                                                </button>
                                                                <span className="w-12 text-center font-semibold">{item.quantity}</span>
                                                                <button
                                                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                                    className="w-8 h-8 border rounded-lg hover:bg-gray-50 flex items-center justify-center"
                                                                >
                                                                    <FaPlus className="text-sm" />
                                                                </button>
                                                            </div>
                                                            <button
                                                                onClick={() => removeItem(item.id)}
                                                                className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
                                                            >
                                                                <FaTrash /> Remove
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="p-6 bg-gray-50 border-t">
                                        <Link to="/products" className="text-purple-600 hover:text-purple-700 inline-flex items-center gap-2">
                                            <FaArrowLeft /> Continue Shopping
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Order Summary */}
                        {cartItems.length > 0 && (
                            <div className="lg:col-span-1">
                                <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
                                    <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>
                                    
                                    <div className="space-y-3 mb-4">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Subtotal</span>
                                            <span className="font-semibold">£{subtotal.toFixed(2)}</span>
                                        </div>
                                        {/* <div className="flex justify-between">
                                            <span className="text-gray-600">Shipping</span>
                                            <span className="font-semibold">{shipping === 0 ? 'Free' : `£{shipping.toFixed(2)}`}</span>
                                        </div> */}
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Tax (10%)</span>
                                            <span className="font-semibold">£{tax.toFixed(2)}</span>
                                        </div>
                                        {promoApplied && (
                                            <div className="flex justify-between text-green-600">
                                                <span>Discount (10%)</span>
                                                <span>-£{discount.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="border-t pt-3 mt-3">
                                            <div className="flex justify-between">
                                                <span className="text-lg font-bold text-gray-800">Total</span>
                                                <span className="text-2xl font-bold text-purple-600">£{total.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Promo Code */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Promo Code</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={promoCode}
                                                onChange={(e) => setPromoCode(e.target.value)}
                                                placeholder="Enter code"
                                                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            />
                                            <button
                                                onClick={applyPromoCode}
                                                disabled={promoApplied}
                                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Try: SAVE10 or FREESHIP</p>
                                    </div>

                                    <button
                                        onClick={handleCheckout}
                                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition transform hover:scale-105"
                                    >
                                        Proceed to Checkout
                                    </button>

                                    <div className="mt-4 text-center text-xs text-gray-500">
                                        <p>Secure payment guaranteed</p>
                                        <div className="flex justify-center gap-2 mt-2">
                                            <FaCreditCard className="text-xl" />
                                            <FaPaypal className="text-xl" />
                                            <FaAmazon className="text-xl" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Shipping Information Step */}
                {checkoutStep === 2 && (
                    <div className="max-w-3xl mx-auto">
                        <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">Shipping Information</h2>
                            <form onSubmit={handleShippingSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                                        <input
                                            type="text"
                                            required
                                            value={shippingInfo.firstName}
                                            onChange={(e) => setShippingInfo({...shippingInfo, firstName: e.target.value})}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                                        <input
                                            type="text"
                                            required
                                            value={shippingInfo.lastName}
                                            onChange={(e) => setShippingInfo({...shippingInfo, lastName: e.target.value})}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                                        <input
                                            type="email"
                                            required
                                            value={shippingInfo.email}
                                            onChange={(e) => setShippingInfo({...shippingInfo, email: e.target.value})}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                                        <input
                                            type="tel"
                                            required
                                            value={shippingInfo.phone}
                                            onChange={(e) => setShippingInfo({...shippingInfo, phone: e.target.value})}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                                        <input
                                            type="text"
                                            required
                                            value={shippingInfo.address}
                                            onChange={(e) => setShippingInfo({...shippingInfo, address: e.target.value})}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                                        <input
                                            type="text"
                                            required
                                            value={shippingInfo.city}
                                            onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                                        <input
                                            type="text"
                                            required
                                            value={shippingInfo.state}
                                            onChange={(e) => setShippingInfo({...shippingInfo, state: e.target.value})}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                                        <input
                                            type="text"
                                            required
                                            value={shippingInfo.zipCode}
                                            onChange={(e) => setShippingInfo({...shippingInfo, zipCode: e.target.value})}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                                        <select
                                            value={shippingInfo.country}
                                            onChange={(e) => setShippingInfo({...shippingInfo, country: e.target.value})}
                                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        >
                                            <option value="US">United States</option>
                                            <option value="UK">United Kingdom</option>
                                            <option value="CA">Canada</option>
                                            <option value="AU">Australia</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={backToCart}
                                        className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                                    >
                                        Back to Cart
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 rounded-lg font-semibold hover:shadow-lg transition"
                                    >
                                        Continue to Payment
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Payment Step */}
                {checkoutStep === 3 && (
                    <div className="max-w-3xl mx-auto">
                        <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">Payment Method</h2>
                            
                            {/* Order Summary */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                <h3 className="font-semibold text-gray-800 mb-2">Order Summary</h3>
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <span>Subtotal:</span>
                                        <span>£{subtotal.toFixed(2)}</span>
                                    </div>
                                    {/* <div className="flex justify-between">
                                        <span>Shipping:</span>
                                        <span>{shipping === 0 ? 'Free' : `£{shipping.toFixed(2)}`}</span>
                                    </div> */}
                                    <div className="flex justify-between">
                                        <span>Tax:</span>
                                        <span>£{tax.toFixed(2)}</span>
                                    </div>
                                    {promoApplied && (
                                        <div className="flex justify-between text-green-600">
                                            <span>Discount:</span>
                                            <span>-£{discount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="border-t pt-2 mt-2">
                                        <div className="flex justify-between font-bold">
                                            <span>Total:</span>
                                            <span className="text-purple-600">£{total.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handlePaymentSubmit}>
                                <div className="space-y-4 mb-6">
                                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            value="credit-card"
                                            checked={paymentMethod === 'credit-card'}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            className="text-purple-600"
                                        />
                                        <FaCreditCard className="text-xl text-gray-600" />
                                        <div>
                                            <span className="font-semibold">Credit / Debit Card</span>
                                            <p className="text-xs text-gray-500">Visa, Mastercard, American Express</p>
                                        </div>
                                    </label>
                                    
                                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            value="paypal"
                                            checked={paymentMethod === 'paypal'}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            className="text-purple-600"
                                        />
                                        <FaPaypal className="text-xl text-blue-600" />
                                        <div>
                                            <span className="font-semibold">PayPal</span>
                                            <p className="text-xs text-gray-500">Fast and secure</p>
                                        </div>
                                    </label>
                                    
                                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="radio"
                                            value="amazon"
                                            checked={paymentMethod === 'amazon'}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            className="text-purple-600"
                                        />
                                        <FaAmazon className="text-xl text-orange-500" />
                                        <div>
                                            <span className="font-semibold">Amazon Pay</span>
                                            <p className="text-xs text-gray-500">Use your Amazon account</p>
                                        </div>
                                    </label>
                                </div>

                                {paymentMethod === 'credit-card' && (
                                    <div className="space-y-4 mb-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
                                            <input
                                                type="text"
                                                placeholder="1234 5678 9012 3456"
                                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                required
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                                                <input
                                                    type="text"
                                                    placeholder="MM/YY"
                                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                                                <input
                                                    type="text"
                                                    placeholder="123"
                                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name</label>
                                            <input
                                                type="text"
                                                placeholder="John Doe"
                                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setCheckoutStep(2)}
                                        className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 rounded-lg font-semibold hover:shadow-lg transition"
                                    >
                                        Place Order (£{total.toFixed(2)})
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartPage;